// The icons the panel draws itself rather than taking from lucide. The design
// takes its icons from Hugeicons' stroke-rounded set
// (https://hugeicons.com/icons/stroke-rounded), so a new icon comes from
// there: paste the paths in as a component here, stroked with `currentColor`,
// rather than reaching for the nearest lucide lookalike. The delete bin and
// the edit pencil were traced from the meal plan assets in
// `src/assets/svg/meal plan`, which are that set's `delete-02` and
// `pencil-edit-02`.
//
// Each asset ships an active and an inactive copy, and the two are the same
// outline — only the stroke colour differs (the pencil dark on a pale pill and
// pale on a filled one; the bin red when it will delete and grey when it will
// not). So there is one component per icon here, stroked with `currentColor`,
// and the state is whatever colour the call site is already setting on the
// button. Each keeps the viewBox and stroke width of the file it came from, so
// the weight is the designed one at whatever size the class asks for.

import React from "react";

export type IconProps = React.SVGProps<SVGSVGElement>;

const baseProps = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  "aria-hidden": true,
} as const;

/** A lidded bin: handle, lid, tapered body, two ribs. */
export const TrashIcon: React.FC<IconProps> = ({
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 20 20"
    strokeWidth={1.25}
    className={className}
    {...props}
  >
    <path d="M16.25 4.58398L15.7336 12.9382C15.6016 15.0727 15.5357 16.1399 15.0007 16.9072C14.7361 17.2866 14.3956 17.6067 14.0006 17.8473C13.2017 18.334 12.1325 18.334 9.99392 18.334C7.8526 18.334 6.78192 18.334 5.98254 17.8464C5.58733 17.6054 5.24667 17.2847 4.98223 16.9047C4.4474 16.1362 4.38287 15.0674 4.25384 12.93L3.75 4.58398" />
    <path d="M2.5 4.58268H17.5M13.3797 4.58268L12.8109 3.40912C12.433 2.62957 12.244 2.23978 11.9181 1.99669C11.8458 1.94277 11.7693 1.8948 11.6892 1.85327C11.3283 1.66602 10.8951 1.66602 10.0287 1.66602C9.14067 1.66602 8.69667 1.66602 8.32973 1.86112C8.24842 1.90436 8.17082 1.95427 8.09774 2.01032C7.76803 2.26327 7.58386 2.66731 7.21551 3.4754L6.71077 4.58268" />
    <path d="M7.91699 13.75V8.75" />
    <path d="M12.083 13.75V8.75" />
  </svg>
);

/** A pencil lying on its diagonal, nib to the bottom left. */
export const PencilIcon: React.FC<IconProps> = ({
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 18 18"
    strokeWidth={1}
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M4.43434 11.894L4 14.5L6.60603 14.0657C7.05858 13.9903 7.47625 13.7753 7.80066 13.4509L13.6777 7.57382C14.1075 7.14401 14.1075 6.44717 13.6776 6.01737L12.4826 4.82235C12.0528 4.39254 11.3559 4.39255 10.9261 4.82237L5.04912 10.6994C4.72471 11.0238 4.50976 11.4415 4.43434 11.894Z" />
    <path d="M10.1113 6.16699L12.3336 8.38922" />
  </svg>
);

/**
 * A padlock, the Hugeicons stroke-rounded `square-lock-01` — the icon the
 * design uses — with `open` swapping in `square-unlock-01`. The two share the
 * body and keyhole; only the shackle differs, swung up and out to the right
 * once undone. Traced rather than taken from a package, like the bin and
 * pencil above, so it is stroked with `currentColor` at the design's weight.
 */
export const LockIcon: React.FC<IconProps & { open?: boolean }> = ({
  open = false,
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M12 14V17.0023" />
    {open ? (
      <path d="M7.5 9V6.5C7.5 4.01472 9.51472 2 12 2C13.9593 2 15.8664 3.25221 16.5 5" />
    ) : (
      <path d="M16.5 9V6.5C16.5 4.01472 14.4853 2 12 2C9.51472 2 7.5 4.01472 7.5 6.5V9" />
    )}
    <path d="M4.26781 18.8447C4.49269 20.515 5.87613 21.8235 7.55966 21.9009C8.97627 21.966 10.4153 22 12 22C13.5847 22 15.0237 21.966 16.4403 21.9009C18.1239 21.8235 19.5073 20.515 19.7322 18.8447C19.879 17.7547 20 16.6376 20 15.5C20 14.3624 19.879 13.2453 19.7322 12.1553C19.5073 10.485 18.1239 9.17649 16.4403 9.09909C15.0237 9.03397 13.5847 9 12 9C10.4153 9 8.97627 9.03397 7.55966 9.09909C5.87613 9.17649 4.49269 10.485 4.26781 12.1553C4.12104 13.2453 4 14.3624 4 15.5C4 16.6376 4.12104 17.7547 4.26781 18.8447Z" />
  </svg>
);

/* ---------------------------------------------------------------------------
 * My task's glyphs, traced from Hugeicons' stroke-rounded set at the sizes
 * that set draws them: viewBox 24, stroke 1.5, `currentColor`. They are here
 * rather than in the feature because the rail's own icon is one of them, and
 * a nav glyph has to be reachable from `navIcons` without pulling a screen in
 * behind it.
 * ------------------------------------------------------------------------ */

/* The rail's My task glyph is not here. It was, as this set's `task-01`
 * traced in, because the sidebar had no asset for a screen that did not exist
 * yet. The design team's own file arrived — `assets/svg/sidebar/my task.svg`
 * — and that wins: it is drawn at the rail's 18px rather than scaled down
 * from 24, and it already carries the sidebar ink. See `navIcons`.
 */

/** `calendar-03` — a month grid with three days marked. */
export const CalendarIcon: React.FC<IconProps> = ({
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M16 2V6M8 2V6" />
    <path d="M13 4H11C7.22876 4 5.34315 4 4.17157 5.17157C3 6.34315 3 8.22876 3 12V14C3 17.7712 3 19.6569 4.17157 20.8284C5.34315 22 7.22876 22 11 22H13C16.7712 22 18.6569 22 19.8284 20.8284C21 19.6569 21 17.7712 21 14V12C21 8.22876 21 6.34315 19.8284 5.17157C18.6569 4 16.7712 4 13 4Z" />
    <path d="M3 10H21" />
    <path d="M12.1258 14H12.0008M12.1258 18H12.0008M7.625 14H7.5M7.625 18H7.5M16.625 14H16.5M12.2508 14C12.2508 14.1381 12.1389 14.25 12.0008 14.25C11.8628 14.25 11.7508 14.1381 11.7508 14C11.7508 13.8619 11.8628 13.75 12.0008 13.75C12.1389 13.75 12.2508 13.8619 12.2508 14ZM12.2508 18C12.2508 18.1381 12.1389 18.25 12.0008 18.25C11.8628 18.25 11.7508 18.1381 11.7508 18C11.7508 17.8619 11.8628 17.75 12.0008 17.75C12.1389 17.75 12.2508 17.8619 12.2508 18ZM7.75 14C7.75 14.1381 7.63807 14.25 7.5 14.25C7.36193 14.25 7.25 14.1381 7.25 14C7.25 13.8619 7.36193 13.75 7.5 13.75C7.63807 13.75 7.75 13.8619 7.75 14ZM7.75 18C7.75 18.1381 7.63807 18.25 7.5 18.25C7.36193 18.25 7.25 18.1381 7.25 18C7.25 17.8619 7.36193 17.75 7.5 17.75C7.63807 17.75 7.75 17.8619 7.75 18ZM16.75 14C16.75 14.1381 16.6381 14.25 16.5 14.25C16.3619 14.25 16.25 14.1381 16.25 14C16.25 13.8619 16.3619 13.75 16.5 13.75C16.6381 13.75 16.75 13.8619 16.75 14Z" />
  </svg>
);

/** `checkmark-circle-02` — a tick in a ring. */
export const CheckCircleIcon: React.FC<IconProps> = ({
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12Z" />
    <path d="M8 12.5L10.5 15L16 9" />
  </svg>
);

/** `cancel-circle` — a cross in a ring. */
export const CancelCircleIcon: React.FC<IconProps> = ({
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12Z" />
    <path d="M14.9994 15L9 9M9.00064 15L15 9" />
  </svg>
);

/** `hourglass` — sand run through, for what is still to come. */
export const HourglassIcon: React.FC<IconProps> = ({
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M4 3H20" />
    <path d="M5.5 3V5.03039C5.5 6.27227 6.07682 7.4437 7.06116 8.20089L12 12L16.9388 8.20089C17.9232 7.44371 18.5 6.27227 18.5 5.03039V3" />
    <path d="M5.5 21V18.9696C5.5 17.7277 6.07682 16.5563 7.06116 15.7991L12 12L16.9388 15.7991C17.9232 16.5563 18.5 17.7277 18.5 18.9696V21" />
    <path d="M4 21H20" />
  </svg>
);

/** `video-01` — the mark an online appointment wears. */
export const VideoIcon: React.FC<IconProps> = ({
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    className={className}
    {...props}
  >
    <path d="M2 11C2 7.70017 2 6.05025 3.02513 5.02513C4.05025 4 5.70017 4 9 4H10C13.2998 4 14.9497 4 15.9749 5.02513C17 6.05025 17 7.70017 17 11V13C17 16.2998 17 17.9497 15.9749 18.9749C14.9497 20 13.2998 20 10 20H9C5.70017 20 4.05025 20 3.02513 18.9749C2 17.9497 2 16.2998 2 13V11Z" />
    <path d="M17 8.90585L17.1259 8.80196C19.2417 7.05623 20.2996 6.18336 21.1498 6.60482C22 7.02628 22 8.42355 22 11.2181V12.7819C22 15.5765 22 16.9737 21.1498 17.3952C20.2996 17.8166 19.2417 16.9438 17.1259 15.198L17 15.0941" />
    <circle cx="11.5" cy="9.5" r="1.5" />
  </svg>
);

/** `location-01` — the mark an in-person one wears in its place. */
export const LocationIcon: React.FC<IconProps> = ({
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    className={className}
    {...props}
  >
    <path d="M13.6177 21.367C13.1841 21.773 12.6044 22 12.0011 22C11.3978 22 10.8182 21.773 10.3845 21.367C6.41302 17.626 1.09076 13.4469 3.68627 7.37966C5.08963 4.09916 8.45834 2 12.0011 2C15.5439 2 18.9126 4.09916 20.316 7.37966C22.9082 13.4393 17.599 17.6389 13.6177 21.367Z" />
    <path d="M15.5 11C15.5 12.933 13.933 14.5 12 14.5C10.067 14.5 8.5 12.933 8.5 11C8.5 9.067 10.067 7.5 12 7.5C13.933 7.5 15.5 9.067 15.5 11Z" />
  </svg>
);

/** `copy-01` — two sheets, for taking the meeting link away. */
export const CopyIcon: React.FC<IconProps> = ({
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M7.5 14.5C7.5 11.2002 7.5 9.55025 8.52513 8.52513C9.55025 7.5 11.2002 7.5 14.5 7.5C17.7998 7.5 19.4497 7.5 20.4749 8.52513C21.5 9.55025 21.5 11.2002 21.5 14.5C21.5 17.7998 21.5 19.4497 20.4749 20.4749C19.4497 21.5 17.7998 21.5 14.5 21.5C11.2002 21.5 9.55025 21.5 8.52513 20.4749C7.5 19.4497 7.5 17.7998 7.5 14.5Z" />
    <path d="M7.5 16.5C6.10355 16.5 5.40533 16.5 4.84402 16.3036C3.83866 15.9518 3.0482 15.1613 2.69641 14.156C2.5 13.5947 2.5 12.8964 2.5 11.5V9.5C2.5 6.20017 2.5 4.55025 3.52513 3.52513C4.55025 2.5 6.20017 2.5 9.5 2.5H11.5C12.8964 2.5 13.5947 2.5 14.156 2.69641C15.1613 3.0482 15.9518 3.83866 16.3036 4.84402C16.5 5.40533 16.5 6.10355 16.5 7.5" />
  </svg>
);

/**
 * `add-01` — two strokes, the plus every "new thing" button in the app wears.
 *
 * It replaces lucide's `Plus` wherever a screen has been through a design
 * pass. The ones still on lucide are not wrong, they are just not swapped yet
 * — see the note at the top of this file.
 */
export const PlusIcon: React.FC<IconProps> = ({
  className = "h-5 w-5",
  ...props
}) => (
  <svg
    {...baseProps}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M12.001 5.00003V19.002" />
    <path d="M19.002 12.002L4.99998 12.002" />
  </svg>
);

/* A bare tick, a bare cross and an exclamation used to live here, traced from
 * `tick-02`, `cancel-01` and `alert-circle` so that My task's timeline could
 * draw a coloured disc and set one of them inside it. The design team's own
 * marks arrived whole — disc, fill and glyph in one file, under
 * `assets/svg/my task/` — and took the job, so these went with them. Each is
 * two minutes' work to bring back; the skill in `.claude/skills/hugeicons`
 * says how.
 */
